import { useStore } from "ra-core";
import { useCallback, useMemo } from "react";
import type { AdvancedFilter } from "./filterTypes";

export const SAVED_FILTERS_STORAGE_KEY = "crm.advanced_saved_filters";
export const ACTIVE_FILTER_STORAGE_KEY = "crm.active_filter_id";

export const AGENDA_SAVED_FILTERS_STORAGE_KEY = "crm.agenda_saved_filters";
export const AGENDA_ACTIVE_FILTER_STORAGE_KEY = "crm.agenda_active_filter_id";

export const defaultAgendaFilters: AdvancedFilter[] = [
  {
    id: "agenda_pendentes",
    name: "Todas as Atividades Pendentes",
    visibility: "shared",
    andConditions: [
      {
        id: "cond_act_1",
        entity: "activity",
        fieldId: "done",
        operator: "é",
        value: "false",
      },
    ],
    orConditions: [],
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: "agenda_alta_prioridade",
    name: "Atividades de Alta Prioridade",
    visibility: "shared",
    andConditions: [
      {
        id: "cond_act_2",
        entity: "activity",
        fieldId: "priority",
        operator: "é",
        value: "alta",
      },
      {
        id: "cond_act_3",
        entity: "activity",
        fieldId: "done",
        operator: "é",
        value: "false",
      },
    ],
    orConditions: [],
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
];

export const defaultFilters: AdvancedFilter[] = [
  {
    id: "abertos",
    name: "Todos os Negócios em Aberto",
    visibility: "shared",
    andConditions: [
      {
        id: "cond_1",
        entity: "deal",
        fieldId: "status",
        operator: "é",
        value: "aberto",
      },
    ],
    orConditions: [],
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: "quentes",
    name: "Negócios Quentes (Alta prioridade)",
    visibility: "shared",
    andConditions: [
      {
        id: "cond_q1",
        entity: "deal",
        fieldId: "temperatura",
        operator: "é",
        value: "quente",
      },
      {
        id: "cond_q2",
        entity: "deal",
        fieldId: "status",
        operator: "é",
        value: "aberto",
      },
    ],
    orConditions: [],
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: "ganhos_recentes",
    name: "Negócios Ganhos",
    visibility: "shared",
    andConditions: [
      {
        id: "cond_g1",
        entity: "deal",
        fieldId: "status",
        operator: "é",
        value: "ganho",
      },
    ],
    orConditions: [],
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
];

export const useSavedFilters = (
  storageKey: string = SAVED_FILTERS_STORAGE_KEY,
  activeKey: string = ACTIVE_FILTER_STORAGE_KEY,
  fallbackFilters: AdvancedFilter[] = defaultFilters,
) => {
  const [filters, setFilters] = useStore<AdvancedFilter[]>(
    storageKey,
    fallbackFilters,
  );

  const [activeFilterId, setActiveFilterId] = useStore<string | null>(
    activeKey,
    null,
  );

  const activeFilter = useMemo(() => {
    if (!activeFilterId) return null;
    return (
      (filters || fallbackFilters).find((f) => f.id === activeFilterId) || null
    );
  }, [filters, activeFilterId, fallbackFilters]);

  const addFilter = useCallback(
    (newFilter: Omit<AdvancedFilter, "id" | "createdAt" | "updatedAt">) => {
      const id = "filter_" + Date.now().toString(36);
      const fullFilter: AdvancedFilter = {
        ...newFilter,
        id,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
      const updated = [...(filters || defaultFilters), fullFilter];
      setFilters(updated);
      setActiveFilterId(id);
      return fullFilter;
    },
    [filters, setFilters, setActiveFilterId],
  );

  const updateFilter = useCallback(
    (id: string, updates: Partial<AdvancedFilter>) => {
      const updated = (filters || defaultFilters).map((f) =>
        f.id === id
          ? { ...f, ...updates, updatedAt: new Date().toISOString() }
          : f,
      );
      setFilters(updated);
    },
    [filters, setFilters],
  );

  const deleteFilter = useCallback(
    (id: string) => {
      const updated = (filters || defaultFilters).filter((f) => f.id !== id);
      setFilters(updated);
      if (activeFilterId === id) {
        setActiveFilterId(null);
      }
    },
    [activeFilterId, filters, setFilters, setActiveFilterId],
  );

  return {
    filters: filters || defaultFilters,
    activeFilter,
    activeFilterId,
    setActiveFilterId,
    addFilter,
    updateFilter,
    deleteFilter,
  };
};
